import { lazy } from "react";
import { createBrowserRouter, Navigate, Outlet } from "react-router-dom";
import { globalStorage } from "../utils/Storage";
import { ADD_PRODUCT_SMENU, EDIT_PRODUCT_SMENU, getSellerMenu, TRANS_SMENU } from "../utils/ConstantSeller";
import { AuthLazyComponent } from "../components/CustomComponent/RoutersComponent";
import { TRANS_UMENU, UMENU_ROUTES } from "../utils/ConstantUser";
import { isDomain } from "../controller/authController";

// component
const MainLayout = lazy(() => import("../layout/MainLayout"));
const Login = lazy(() => import("../pages/Login"));
const NoMatch = lazy(() => import("../pages/NoMatchPage"));
const ResetPassword = lazy(() => import("../pages/ResetPassword"));
const Print = lazy(() => import("../pages/Print"));
const ProfileUser = lazy(() => import("../pages/ProfileUser"));
const ProfileSeller = lazy(() => import("../pages/ProfileSeller"));
const Register = lazy(() => import("../pages/Register"));
const Search = lazy(() => import("../pages/Search"));
const Shipment = lazy(() => import("../pages/Shipment"));
const CommunityListHome = lazy(() => import("../pages/CommunityListHome"));
const TermAndPolicy = lazy(() => import("../pages/TermAndPolicy"));
const Cart = lazy(() => import("../pages/Cart"));
const DetailProduct = lazy(() => import("../pages/ProductDetail"));

const StoreHome = lazy(() => import("../pages/StoreHome"));
const MerchantHome = lazy(() => import("../pages/MerchantHome"));
const CreateStore = lazy(() => import("../pages/CreateStore"));
const CommunityHome = lazy(() => import("../pages/CommunityHome"));
const EcosystemHome = lazy(() => import("../pages/EcosystemHome"));


const LandingPage = ({ c_flag = false, s_flag = false, eco_flag = false }) => {
  if (c_flag) return <CommunityHome />;
  if (eco_flag) return <EcosystemHome />;
  if (s_flag && !c_flag && !eco_flag) return <StoreHome />;
  return <MerchantHome />;
};

const RestrictPage = ({ flag = false, component, props = {} }) => {
  const Component = flag ? component : NoMatch
  return <Component {...props} />
}

const RestricSellerDashboard = ({ sub_seller, cs_seller }) => {
  const acc_from = isDomain ? "domain" : "subdomain";
  const userData = globalStorage?.getItem("UD");
  const current_store = globalStorage?.getItem("SD");
  const own_store = userData?.seller?.[acc_from] ?? "";

  if (current_store?.[acc_from] !== own_store && own_store !== "") return <Navigate replace to="/" />;

  const admin_user = userData?.user_level_id === 2 && (userData?.seller?.is_private ?? 0);
  const componentProps = { sub_seller, admin_user, cs_flag: cs_seller }
  return <AuthLazyComponent component={ProfileSeller} props={componentProps} />
};

const StoreHomeComponent = ({ s_flag = false, props = {} }) => {
  const Component = s_flag ? StoreHome : NoMatch
  return <Component {...props} />
}

const CreateStoreComponent = ({ s_flag = false, eco_flag = false, props = {} }) => {
  const Component = (!s_flag || eco_flag) ? CreateStore : NoMatch
  return <Component {...props} />
}


const routes = ({ reduxState }) => {
  const current_store = globalStorage?.getItem("SD");
  const globalRedux = reduxState?.global || undefined

  const s_flag = current_store !== undefined;
  const c_flag = globalRedux?.communityFlag || false;
  const cg_flag = globalRedux?.cgFlag || false;

  const seller = globalRedux?.userData?.seller;
  const sub_seller = globalRedux?.userData?.user_level_id === 8;
  const cs_seller = globalRedux?.userData?.user_level_id === 9;

  const eco_flag = current_store?.is_ecosystems === 1 ?? false;
  const seller_menu = getSellerMenu(cg_flag, sub_seller);

  const store_nav = cs_seller
    ? "/seller_dashboard/transaction/confrim"
    : (sub_seller ? "/seller_dashboard/performance" : "/seller_dashboard");
  const s_nav = seller !== null ? store_nav : "/";
  const restricSellerDashboardParam = { sub_seller, cs_seller }

  return createBrowserRouter([{
    path: '/',
    element: <MainLayout />,
    children: [
      { index: true, element: <LandingPage {...{ c_flag, s_flag, eco_flag }} /> },
      { path: "*", element: <NoMatch /> },

      // common
      { path: 'login', element: <Login /> },
      { path: 'privacy', element: <TermAndPolicy type="privasi" /> },
      { path: 'register', element: <Register /> },
      { path: 'reset_password', element: <ResetPassword /> },
      { path: "search", element: <Search /> },
      { path: 'terms', element: <TermAndPolicy type="term" /> },

      // products
      {
        path: "category",
        children: [
          { index: true, element: <StoreHomeComponent s_flag={s_flag} /> },
          { path: ":id", element: <StoreHomeComponent s_flag={s_flag} /> },
          { path: ":id/:sub_id", element: <StoreHomeComponent s_flag={s_flag} /> },
        ]
      },
      { path: "product/:product_id", element: <DetailProduct /> },

      // auth
      { path: "print/:type/:inv", element: <AuthLazyComponent component={Print} /> },
      {
        path: "cart",
        children: [
          { index: true, element: <AuthLazyComponent component={Cart} /> },
          { path: "shipment", element: <AuthLazyComponent component={Shipment} /> },
        ],
      },

      // USER PROFILE ROUTES
      {
        path: "/user_profile",
        children: UMENU_ROUTES.map((data) => {
          const flag = data === TRANS_UMENU;
          const path = flag ? `${data}/:process` : data;
          return { path, element: <AuthLazyComponent component={ProfileUser} /> };
        }),
      },

      // SELLER DASHBOARD
      {
        path: "/seller_dashboard",
        children: [
          ...(sub_seller || cs_seller
            ? [{ index: true, element: <Navigate replace to={s_nav} /> }]
            : []),

          { path: "*", element: <Navigate replace to={s_nav} /> },
          ...(seller !== null ? seller_menu.map((data) => {
            let path = data;
            if (data === TRANS_SMENU) path = `${data}/:process`;
            if (data === EDIT_PRODUCT_SMENU) path = `${data}/:product_id`;
            return { path, element: <RestricSellerDashboard {...restricSellerDashboardParam} /> };
          }) : []),

          { path: `${ADD_PRODUCT_SMENU}/:product_id`, element: <RestricSellerDashboard {...restricSellerDashboardParam} /> },
        ],
      },

      // OUTSIDE STORE ROUTES
      { path: "/create_store", element: <CreateStoreComponent {...{ s_flag, eco_flag }} /> },
      {
        path: "/community_home",
        children: [
          { index: true, element: <RestrictPage flag={!s_flag} component={CommunityListHome} /> },
          { path: ":seller_id", element: <RestrictPage flag={!s_flag || eco_flag} component={CommunityListHome} /> },
        ],
      },
    ]
  }], { future: { v7_normalizeFormMethod: true } })
}

export default routes;